import { isNative } from "@/lib/capacitor";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";

export async function initializeDeepLinks(): Promise<void> {
  if (!isNative) return;
  const { App } = await import("@capacitor/app");

  // Cold-start case: when the app is launched FROM a deep link (not running
  // beforehand), the appUrlOpen event can fire before the JS listener is
  // registered. App.getLaunchUrl() returns whatever URL booted the app so we
  // never miss the OAuth callback on first launch.
  //
  // Guard against re-processing the same launch URL after a WebView reload
  // (e.g. window.location.href = "/somewhere"). Without this, the original
  // OAuth callback URL gets passed back to handleDeepLink → Supabase tries
  // to exchange a code whose verifier was consumed on the first run →
  // "PKCE code verifier not found in storage" toast.
  try {
    const launch = await App.getLaunchUrl();
    if (launch?.url) {
      const seenKey = "fig.deepLinkLaunchHandled";
      const seen = sessionStorage.getItem(seenKey);
      if (seen !== launch.url) {
        sessionStorage.setItem(seenKey, launch.url);
        await handleDeepLink(launch.url);
      }
    }
  } catch (err) {
    console.warn("[deepLinks] getLaunchUrl failed", err);
  }

  await App.addListener("appUrlOpen", ({ url }) => {
    void handleDeepLink(url);
  });
}

async function handleDeepLink(url: string): Promise<void> {
  try {
    const u = new URL(url);
    // OAuth flows redirect to fig://login-callback. There's no path on a
    // custom-scheme URL — the host is `login-callback`. After exchange we
    // land the SPA on /auth/callback so its post-OAuth logic (waitlist
    // approval check, onboard routing, etc.) still runs.
    const isLoginCallback = u.host === "login-callback";

    // PKCE: ?code=...
    const code = u.searchParams.get("code");
    if (code) {
      // exchangeCodeForSession takes the bare code string, NOT the full URL.
      // Passing the whole URL silently fails (Supabase treats the URL itself
      // as the code, the swap rejects, and the user never gets a session).
      const { error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) {
        console.warn("[deepLinks] exchangeCodeForSession failed", error);
        toast.error(error.message ?? "Sign-in failed. Please try again.");
      } else {
        // Confirm a session is resident so the SPA's auth subscribers see
        // SIGNED_IN before navigation. exchangeCodeForSession sets it, but
        // this surfaces any race condition early.
        await supabase.auth.getSession();
      }
      const targetPath = isLoginCallback ? "/auth/callback" : u.pathname;
      const params = new URLSearchParams(u.search);
      params.delete("code");
      params.delete("state");
      const qs = params.toString();
      replaceRoute(`${targetPath}${qs ? `?${qs}` : ""}`);
      return;
    }

    // Implicit / magic-link / recovery: #access_token=...&refresh_token=...
    if (u.hash) {
      const params = new URLSearchParams(u.hash.replace(/^#/, ""));
      const access_token = params.get("access_token");
      const refresh_token = params.get("refresh_token");
      if (access_token && refresh_token) {
        const { error } = await supabase.auth.setSession({ access_token, refresh_token });
        if (error) {
          console.warn("[deepLinks] setSession failed", error);
          toast.error(error.message ?? "Sign-in failed. Please try again.");
        } else {
          await supabase.auth.getSession();
        }
      }
      const targetPath = isLoginCallback ? "/auth/callback" : u.pathname;
      replaceRoute(`${targetPath}${u.search}`);
      return;
    }

    // No auth payload — just route to the path.
    const targetPath = isLoginCallback ? "/auth/callback" : u.pathname;
    replaceRoute(`${targetPath}${u.search}`);
  } catch (err) {
    console.warn("[deepLinks] handleDeepLink parse failed", err);
  }
}

function replaceRoute(next: string): void {
  if (next && next !== window.location.pathname + window.location.search) {
    window.history.replaceState({}, "", next);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
}
